import React from 'react';
import axios from 'axios';
import { Avatar, Button, Card, Checkbox, Col, Divider, Modal, Row, Select, Space, Switch, Tag, Typography, Upload, message, Spin } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { QRCodeSVG } from 'qrcode.react';
import dayjs from 'dayjs';

const { Text } = Typography;

interface ProfileTabProps {
  email?: string;
  avatarUrl?: string;
  isMaster: boolean;
  effectiveRoleCode: string;
  effectiveRoleLabel: string;
  roleTagColor: string;
  effectiveCompanyName: string;
  onLogout: () => void | Promise<void>;
  getRoleIcon: (code: string) => React.ReactNode;
}

const ProfileTab: React.FC<ProfileTabProps> = ({
  email,
  avatarUrl,
  isMaster,
  effectiveRoleCode,
  effectiveRoleLabel,
  roleTagColor,
  effectiveCompanyName,
  onLogout,
  getRoleIcon,
}) => {
  const { currentUser, loginUser } = useAuth();
  const { themeMode, setThemeMode } = useTheme();
  const [uploading, setUploading] = React.useState(false);
  const [metricsLoading, setMetricsLoading] = React.useState(false);
  const [areasLoading, setAreasLoading] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [whatsAppLoading, setWhatsAppLoading] = React.useState(false);
  const [whatsAppLink, setWhatsAppLink] = React.useState<string | null>(null);
  const [whatsAppMessage, setWhatsAppMessage] = React.useState<string | null>(null);
  const [whatsAppExpiresAt, setWhatsAppExpiresAt] = React.useState<string | null>(null);
  const [whatsAppNumbersLoading, setWhatsAppNumbersLoading] = React.useState(false);
  const [whatsAppNumbers, setWhatsAppNumbers] = React.useState<Array<{
    id: number;
    phoneNumber: string;
    status: string;
    verifiedAt?: string | null;
    lastMessageAt?: string | null;
    createdAt?: string | null;
  }>>([]);
  const [metricOptions, setMetricOptions] = React.useState<Array<{ label: string; value: string }>>([]);
  const [metricDefinitions, setMetricDefinitions] = React.useState<Array<{ key: string; name?: string; valueTypeKey?: string; sourceKey?: string }>>([]);
  const [areaOptions, setAreaOptions] = React.useState<Array<{ label: string; value: number }>>([]);
  const [selectedMetrics, setSelectedMetrics] = React.useState<string[]>([]);
  const [savedMetricKeys, setSavedMetricKeys] = React.useState<string[]>([]);
  const [favoriteAreaId, setFavoriteAreaId] = React.useState<number | null>(null);
  const avatarSrc = avatarUrl || currentUser?.avatarUrl;

  React.useEffect(() => {
    const settings = currentUser?.dashboardSettings;
    setSavedMetricKeys(settings?.metrics || []);
    setFavoriteAreaId(settings?.favoriteAreaId ?? null);
  }, [currentUser?.dashboardSettings]);

  React.useEffect(() => {
    if (metricDefinitions.length === 0) {
      setSelectedMetrics(savedMetricKeys);
      return;
    }
    const resolved = new Set<string>();
    savedMetricKeys.forEach((value) => {
      const valueLower = value.toLowerCase();
      const match = metricDefinitions.find((metric) => {
        const aliases = [metric.key, metric.valueTypeKey, metric.sourceKey].filter(Boolean) as string[];
        return aliases.some((alias) => alias.toLowerCase() === valueLower);
      });
      const resolvedKey = match?.valueTypeKey || match?.sourceKey || match?.key || value;
      resolved.add(resolvedKey.toLowerCase());
    });
    setSelectedMetrics(Array.from(resolved));
  }, [savedMetricKeys, metricDefinitions]);

  React.useEffect(() => {
    const loadMetrics = async () => {
      try {
        setMetricsLoading(true);
        const response = await axios.get(`${ENDPOINTS.METRICS}?active=true`, { withCredentials: true });
        const metrics = response.data?.data || [];
        const normalized = metrics
          .filter((metric: any) => typeof metric?.key === 'string')
          .map((metric: any) => ({
            key: metric.key,
            name: metric.name || metric.key,
            valueTypeKey: metric.valueTypeKey || undefined,
            sourceKey: metric.sourceKey || undefined,
          }));
        setMetricDefinitions(normalized);

        const optionsMap = new Map<string, { label: string; value: string }>();
        normalized.forEach((metric: { key: string; name?: string; valueTypeKey?: string; sourceKey?: string }) => {
          const value = (metric.valueTypeKey || metric.sourceKey || metric.key).toLowerCase();
          if (!optionsMap.has(value)) {
            optionsMap.set(value, { value, label: metric.name || metric.key });
          }
        });
        const options = Array.from(optionsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
        setMetricOptions(options);
      } catch (error) {
        console.error('Failed to load metrics', error);
        message.error('Unable to load metrics for dashboard settings.');
      } finally {
        setMetricsLoading(false);
      }
    };

    const loadAreas = async () => {
      try {
        setAreasLoading(true);
        const response = await axios.get(`${ENDPOINTS.AREAS_V2}?limit=1000`, { withCredentials: true });
        const areas = response.data?.data || [];
        const byId = new Map<number, any>();
        areas.forEach((area: any) => {
          if (typeof area?.id === 'number') {
            byId.set(area.id, area);
          }
        });

        const buildPath = (areaId: number) => {
          const names: string[] = [];
          const visited = new Set<number>();
          let current = byId.get(areaId);
          while (current && !visited.has(current.id)) {
            visited.add(current.id);
            if (current.name) {
              names.unshift(current.name);
            }
            const parentId = current.parentAreaId;
            current = typeof parentId === 'number' ? byId.get(parentId) : null;
          }
          return names.join(' / ');
        };

        const options = areas
          .filter((area: any) => typeof area?.id === 'number')
          .map((area: any) => ({
            value: area.id as number,
            label: buildPath(area.id as number) || area.name || `Area ${area.id}`,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
        setAreaOptions(options);
      } catch (error) {
        console.error('Failed to load areas', error);
        message.error('Unable to load areas for dashboard settings.');
      } finally {
        setAreasLoading(false);
      }
    };

    loadMetrics();
    loadAreas();
  }, []);

  const handleSaveDashboardSettings = async () => {
    try {
      setSavingSettings(true);
      const payload = {
        metrics: selectedMetrics.map((value) => value.toLowerCase()),
        favoriteAreaId: favoriteAreaId ?? null,
      };
      const response = await axios.patch(ENDPOINTS.UPDATE_DASHBOARD_SETTINGS, payload, { withCredentials: true });
      const savedSettings = response.data?.dashboardSettings || payload;
      if (currentUser) {
        loginUser({ ...currentUser, dashboardSettings: savedSettings });
      }
      message.success('Dashboard settings saved.');
    } catch (error: any) {
      console.error('Failed to save dashboard settings', error);
      message.error(error?.response?.data?.error || 'Unable to save dashboard settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const uploadProps: UploadProps = {
    showUploadList: false,
    accept: 'image/*',
    beforeUpload: (file) => {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        message.error('Only JPEG, PNG, WebP, and SVG images are allowed.');
        return Upload.LIST_IGNORE;
      }
      const maxSizeMb = 5;
      if (file.size / 1024 / 1024 > maxSizeMb) {
        message.error('Avatar file must be smaller than 5MB.');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        setUploading(true);
        const formData = new FormData();
        formData.append('avatar', file as File);
        const response = await axios.post(ENDPOINTS.UPLOAD_USER_AVATAR, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const uploadedUrl = response.data?.avatarUrl;
        if (uploadedUrl && currentUser) {
          loginUser({ ...currentUser, avatarUrl: uploadedUrl });
        }
        message.success('Profile photo updated.');
        onSuccess?.(response.data, new XMLHttpRequest());
      } catch (error: any) {
        console.error('Profile photo upload failed', error);
        message.error(error?.response?.data?.message || 'Failed to upload profile photo.');
        onError?.(error);
      } finally {
        setUploading(false);
      }
    },
  };

  const handleGenerateWhatsAppQr = async () => {
    try {
      setWhatsAppLoading(true);
      const response = await axios.post(
        ENDPOINTS.WHATSAPP_QR,
        { context: { source: 'settings' } },
        { withCredentials: true },
      );
      const data = response.data?.data || {};
      setWhatsAppLink(data.whatsappLink || null);
      setWhatsAppMessage(data.message || null);
      setWhatsAppExpiresAt(data.expiresAt || null);
      message.success('WhatsApp QR generated.');
    } catch (error: any) {
      console.error('Failed to generate WhatsApp QR', error);
      message.error(error?.response?.data?.message || 'Unable to generate WhatsApp QR.');
    } finally {
      setWhatsAppLoading(false);
    }
  };

  const loadWhatsAppNumbers = React.useCallback(async () => {
    try {
      setWhatsAppNumbersLoading(true);
      const response = await axios.get(ENDPOINTS.WHATSAPP_NUMBERS, { withCredentials: true });
      setWhatsAppNumbers(response.data?.data || []);
    } catch (error) {
      console.error('Failed to load WhatsApp numbers', error);
    } finally {
      setWhatsAppNumbersLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadWhatsAppNumbers();
  }, [loadWhatsAppNumbers]);

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label} copied.`);
    } catch (error) {
      message.error('Unable to copy to clipboard.');
    }
  };

  return (
    <Card
      className="configuration-profile-card"
      title={
      <Space>
        <UserOutlined />
        Profile Information
      </Space>
    }>
      <Row gutter={[16, 16]} className="configuration-profile-row">
        <Col xs={24} sm={24} md={4} className="configuration-profile-avatar-col">
          <Space direction="vertical" size="small" align="center" style={{ width: '100%' }}>
            <Avatar size={72} src={avatarSrc} icon={!avatarSrc ? <UserOutlined /> : undefined} />
            <Upload {...uploadProps}>
              <Button size="small" loading={uploading}>
                Change photo
              </Button>
            </Upload>
          </Space>
        </Col>
        <Col xs={24} sm={24} md={20} className="configuration-profile-identity">
          <Space direction="vertical" size="small">
            <div>
              <Text strong>Email: </Text>
              <Text>{email}</Text>
            </div>
            <div>
              <Text strong>Role: </Text>
              <Space>
                {getRoleIcon(isMaster ? 'master' : effectiveRoleCode)}
                {isMaster && <Tag color="gold">Master</Tag>}
                <Tag color={roleTagColor}>{effectiveRoleLabel}</Tag>
              </Space>
            </div>
            <div>
              <Text strong>Company: </Text>
              <Text>{effectiveCompanyName}</Text>
            </div>
          </Space>
        </Col>
      </Row>

      <Divider />

      <div className="configuration-profile-appearance-section">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text strong>Appearance</Text>
          <div className="configuration-profile-appearance-row">
            <div className="configuration-profile-appearance-copy">
              <Text>Dark mode</Text>
              <br />
              <Text type="secondary">Use a darker interface on this browser.</Text>
            </div>
            <Switch
              checked={themeMode === 'dark'}
              checkedChildren="Dark"
              unCheckedChildren="Light"
              aria-label="Toggle dark mode"
              onChange={(checked) => setThemeMode(checked ? 'dark' : 'light')}
            />
          </div>
        </Space>
      </div>

      <Divider />

      <div style={{ marginBottom: 16 }} className="configuration-profile-dashboard-section">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text strong>Dashboard settings</Text>
          <div>
            <Text type="secondary">Metrics to show</Text>
            <div style={{ marginTop: 8 }}>
              {metricsLoading ? (
                <Spin size="small" />
              ) : (
                <Checkbox.Group
                  className="configuration-metrics-checkboxes"
                  options={metricOptions}
                  value={selectedMetrics}
                  onChange={(values) => setSelectedMetrics(values as string[])}
                />
              )}
            </div>
          </div>
          <div>
            <Text type="secondary">Favorite area</Text>
            <div style={{ marginTop: 8 }}>
              {areasLoading ? (
                <Spin size="small" />
              ) : (
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select a favorite area"
                  options={areaOptions}
                  value={favoriteAreaId ?? undefined}
                  onChange={(value) => setFavoriteAreaId(typeof value === 'number' ? value : null)}
                  className="configuration-field-control"
                  style={{ minWidth: 280, width: '100%' }}
                />
              )}
            </div>
          </div>
          <Button
            className="configuration-profile-save-dashboard"
            type="primary"
            loading={savingSettings}
            onClick={handleSaveDashboardSettings}
            style={{ alignSelf: 'flex-start' }}
          >
            Save dashboard settings
          </Button>
        </Space>
      </div>

      <Divider />

      <div style={{ marginBottom: 16 }} className="configuration-profile-whatsapp-section">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text strong>WhatsApp linking</Text>
          <Text type="secondary">
            Link your WhatsApp number to your OSCAR account. Once linked and verified, your number works across
            <strong> all companies</strong> you have access to — you can switch between companies directly in the WhatsApp chat.
          </Text>
          {whatsAppNumbers.some((n) => n.status === 'verified') && (
            <div className="configuration-profile-status-panel">
              <Space>
                <span style={{ color: '#52c41a' }}>✓</span>
                <Text style={{ color: '#389e0d' }}>
                  WhatsApp is linked and active for all your companies.
                  {whatsAppNumbers.filter((n) => n.status === 'verified').map((n) => (
                    <Text key={n.id} code style={{ marginLeft: 8, fontSize: 12 }}>{n.phoneNumber}</Text>
                  ))}
                </Text>
              </Space>
            </div>
          )}
          <Space wrap className="configuration-profile-whatsapp-actions">
            <Button
              className="configuration-profile-generate-qr"
              type={whatsAppNumbers.some((n) => n.status === 'verified') ? 'default' : 'primary'}
              onClick={handleGenerateWhatsAppQr}
              loading={whatsAppLoading}
            >
              {whatsAppNumbers.some((n) => n.status === 'verified') ? 'Link a different number' : 'Generate QR to link'}
            </Button>
            {whatsAppLink && (
              <Button onClick={() => window.open(whatsAppLink, '_blank', 'noopener,noreferrer')}>
                Open WhatsApp
              </Button>
            )}
          </Space>
          {whatsAppLink && (
            <div className="configuration-profile-whatsapp-qr" style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="configuration-profile-qr-frame">
                <QRCodeSVG value={whatsAppLink} size={180} level="M" includeMargin />
              </div>
              <Space direction="vertical" size="small">
                <div>
                  <Text type="secondary">WhatsApp link</Text>
                  <div>
                    <Text code style={{ wordBreak: 'break-all' }}>{whatsAppLink}</Text>
                  </div>
                  <Button size="small" onClick={() => handleCopy(whatsAppLink, 'Link')}>
                    Copy link
                  </Button>
                </div>
                {whatsAppMessage && (
                  <div>
                    <Text type="secondary">Message</Text>
                    <div>
                      <Text code>{whatsAppMessage}</Text>
                    </div>
                    <Button size="small" onClick={() => handleCopy(whatsAppMessage, 'Message')}>
                      Copy message
                    </Button>
                  </div>
                )}
                {whatsAppExpiresAt && (
                  <Text type="secondary">
                    Expires: {dayjs(whatsAppExpiresAt).format('MMM D, YYYY HH:mm')}
                  </Text>
                )}
              </Space>
            </div>
          )}
          <Divider style={{ margin: '12px 0' }} />
          <Space direction="vertical" size="small" className="configuration-profile-whatsapp-linked">
            <Space align="center" wrap>
              <Text strong>Linked numbers</Text>
              <Button size="small" onClick={loadWhatsAppNumbers} loading={whatsAppNumbersLoading}>
                Refresh
              </Button>
            </Space>
            {whatsAppNumbersLoading ? (
              <Spin size="small" />
            ) : (
              <>
                {whatsAppNumbers.length === 0 ? (
                  <Text type="secondary">No linked numbers yet.</Text>
                ) : (
                  <Space direction="vertical" size="small">
                    {whatsAppNumbers.map((entry) => (
                      <div key={entry.id} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Text code>{entry.phoneNumber}</Text>
                        <Tag color={entry.status === 'verified' ? 'green' : entry.status === 'pending' ? 'gold' : 'red'}>
                          {entry.status}
                        </Tag>
                        {entry.verifiedAt && (
                          <Text type="secondary">Verified {dayjs(entry.verifiedAt).format('MMM D, YYYY HH:mm')}</Text>
                        )}
                      </div>
                    ))}
                  </Space>
                )}
              </>
            )}
          </Space>
        </Space>
      </div>

      <Space wrap>
        <Button
          className="configuration-profile-logout-button"
          type="primary"
          danger
          onClick={() => {
            Modal.confirm({
              title: 'Logout',
              content: 'Are you sure you want to logout?',
              onOk: onLogout,
            });
          }}
        >
          Logout
        </Button>
      </Space>
    </Card>
  );
};

export default ProfileTab;
